import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface WidgetExportRow {
  name: string;
  total: number | string;
  part_number?: string;
  description?: string;
}

export interface WidgetExportOptions {
  title: string;
  rows: WidgetExportRow[];
  /** true → values are dollars; false → quantities */
  isCost: boolean;
  /** Human-readable filter context, e.g. { Location: 'All Locations', 'Date Range': '2026-11-01 to 2026-11-03' } */
  filters: Record<string, string>;
  /** The rendered chart's <svg>, used for PNG and the PDF chart image */
  chartSvg?: SVGSVGElement | null;
}

const TEAL_RGB: [number, number, number] = [0, 152, 170];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'widget';

const buildFileName = (opts: WidgetExportOptions, ext: string) => {
  const parts = [slugify(opts.title), ...Object.values(opts.filters).map(slugify)].filter(Boolean);
  return `${parts.join('_')}.${ext}`;
};

const formatValue = (value: number, isCost: boolean) =>
  isCost
    ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : value.toLocaleString('en-US');

/** Full dataset sorted desc, with numeric totals (MySQL DECIMAL arrives as strings). */
const prepareRows = (rows: WidgetExportRow[]) => {
  const normalized = rows
    .map((row) => ({ ...row, total: Number(row.total) || 0 }))
    .sort((a, b) => b.total - a.total);
  const grandTotal = normalized.reduce((sum, row) => sum + row.total, 0);
  const hasPart = normalized.some((row) => row.part_number);
  const hasDescription = normalized.some((row) => row.description);
  return { normalized, grandTotal, hasPart, hasDescription };
};

const buildTable = (opts: WidgetExportOptions, forCsv: boolean) => {
  const { normalized, grandTotal, hasPart, hasDescription } = prepareRows(opts.rows);
  const valueHeader = opts.isCost ? 'Total Cost ($)' : 'Total Quantity';

  const head = ['Rank', 'Name'];
  if (hasPart) head.push('Part Number');
  if (hasDescription) head.push('Description');
  head.push(valueHeader, '% of Total');

  const pct = (value: number) => (grandTotal ? `${((value / grandTotal) * 100).toFixed(1)}%` : '0.0%');
  // CSV keeps raw numbers so the column stays numeric in Excel.
  const fmt = (value: number) => (forCsv ? (opts.isCost ? value.toFixed(2) : String(value)) : formatValue(value, opts.isCost));

  const body = normalized.map((row, index) => {
    const cells: string[] = [String(index + 1), row.name ?? ''];
    if (hasPart) cells.push(row.part_number ?? '');
    if (hasDescription) cells.push(row.description ?? '');
    cells.push(fmt(row.total), pct(row.total));
    return cells;
  });

  const footer = ['', 'Total'];
  if (hasPart) footer.push('');
  if (hasDescription) footer.push('');
  footer.push(fmt(grandTotal), normalized.length ? '100.0%' : '0.0%');

  return { head, body, footer };
};

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const csvCell = (value: string) => `"${String(value).replace(/"/g, '""')}"`;

export const exportWidgetCsv = (opts: WidgetExportOptions) => {
  const { head, body, footer } = buildTable(opts, true);
  const lines = [head, ...body, footer].map((row) => row.map(csvCell).join(','));
  // BOM so Excel opens UTF-8 names correctly.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, buildFileName(opts, 'csv'));
};

/** Rasterize a Recharts <svg> to a PNG data URL on a white background. */
const svgToPngDataUrl = (svg: SVGSVGElement, scale = 2): Promise<{ dataUrl: string; width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const rect = svg.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));

    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', String(width));
    clone.setAttribute('height', String(height));
    clone.style.fontFamily = getComputedStyle(svg).fontFamily || 'Arial, sans-serif';

    const svgString = new XMLSerializer().serializeToString(clone);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, width, height);
      resolve({ dataUrl: canvas.toDataURL('image/png'), width, height });
    };
    img.onerror = () => reject(new Error('Failed to render chart image'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  });

/** PNG of the chart with the title and filters stamped above it. */
export const exportWidgetPng = async (opts: WidgetExportOptions) => {
  if (!opts.chartSvg) throw new Error('Chart is not available to export');
  const scale = 2;
  const chart = await svgToPngDataUrl(opts.chartSvg, scale);
  const chartImg = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to render chart image'));
    img.src = chart.dataUrl;
  });

  const padding = 24;
  const headerHeight = 64;
  const width = chart.width + padding * 2;
  const height = chart.height + headerHeight + padding * 2;

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = '#1a1a1a';
  ctx.font = '600 18px Arial, sans-serif';
  ctx.fillText(opts.title, padding, padding + 16);
  ctx.fillStyle = '#666666';
  ctx.font = '12px Arial, sans-serif';
  const filterLine = Object.entries(opts.filters)
    .map(([key, value]) => `${key}: ${value}`)
    .join('   •   ');
  ctx.fillText(filterLine, padding, padding + 40);

  ctx.drawImage(chartImg, padding, padding + headerHeight, chart.width, chart.height);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Failed to create image');
  triggerDownload(blob, buildFileName(opts, 'png'));
};

/** PDF with title, filters, chart image (if available) and the full data table. */
export const exportWidgetPdf = async (opts: WidgetExportOptions) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = 16;

  doc.setFontSize(16);
  doc.text(opts.title, margin, y);
  y += 7;

  doc.setFontSize(10);
  doc.setTextColor(100);
  Object.entries(opts.filters).forEach(([key, value]) => {
    doc.text(`${key}: ${value}`, margin, y);
    y += 5;
  });
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
  doc.setTextColor(0);
  y += 6;

  if (opts.chartSvg) {
    try {
      const chart = await svgToPngDataUrl(opts.chartSvg);
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = Math.min(110, (chart.height / chart.width) * imgWidth);
      doc.addImage(chart.dataUrl, 'PNG', margin, y, imgWidth, imgHeight);
      y += imgHeight + 6;
    } catch {
      // Chart image is best-effort; the data table below is the authoritative content.
    }
  }

  const { head, body, footer } = buildTable(opts, false);
  const valueColIndex = head.length - 2;
  autoTable(doc, {
    head: [head],
    body,
    foot: [footer],
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
    headStyles: { fillColor: TEAL_RGB, textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
    columnStyles: {
      0: { halign: 'right', cellWidth: 12 },
      [valueColIndex]: { halign: 'right' },
      [valueColIndex + 1]: { halign: 'right', cellWidth: 20 },
    },
    margin: { left: margin, right: margin },
  });

  doc.save(buildFileName(opts, 'pdf'));
};
